import * as Sentry from '@sentry/react';

// Both apps build with Vite into `assetsDir: 'static'`, so every code-split
// chunk is served from /static/<name>-<hash>.js. CRA's older
// /static/js/<name>.<hash>.chunk.js layout lives under /static/ too, so this
// covers a build still on that naming.
const isBuildAsset = url => typeof url === 'string' && /\/static\/.+\.js(\?|$)/.test(url);

/**
 * Detects if an error is a chunk load error (happens after deployments when cached chunks are stale)
 * @param {Error|Object} error - The error object
 * @param {string} filename - Optional filename from error event
 * @returns {boolean}
 */
export const isChunkLoadError = (error, filename = '') => {
    const errorMessage = error?.message || error?.toString() || '';

    // Standard chunk load error patterns (CRA/webpack)
    if (errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError')) {
        return true;
    }

    // Vite dynamic-import failures vary by browser:
    // - Chrome/Edge: "Failed to fetch dynamically imported module"
    // - Firefox: "error loading dynamically imported module"
    // - Safari (older): "Importing a module script failed"
    // - Safari 26+: "'text/html' is not a valid JavaScript MIME type."
    //   (fired when the SPA HTML fallback is served for a missing chunk)
    if (errorMessage.includes('dynamically imported module') ||
        errorMessage.includes('Importing a module script failed') ||
        errorMessage.includes('is not a valid JavaScript MIME type')) {
        return true;
    }

    // A build asset that won't parse is a broken chunk fetch, not an app bug:
    // - "Unexpected token '<'" — the SPA HTML fallback was served for a chunk
    //   that 404'd (stale bundle after a deploy).
    // - "Unexpected end of script" (Safari) / "Unexpected end of input"
    //   (Chrome/Firefox) — the response was truncated mid-body (#1537).
    //   Firebase Hosting omits Content-Length on compressed responses, so a
    //   connection that drops mid-chunk surfaces as a parse error rather than
    //   a network error.
    //
    // Matching the asset path is what keeps this from swallowing a genuine
    // SyntaxError in app code. We read sourceURL (Safari) and fileName
    // (Firefox) as well as window.onerror's filename, because React error
    // boundaries only ever hand us the error object.
    if (error?.name === 'SyntaxError' &&
        (errorMessage.includes('Unexpected token \'<\'') ||
         errorMessage.includes('Unexpected end of script') ||
         errorMessage.includes('Unexpected end of input')) &&
        (isBuildAsset(filename) || isBuildAsset(error?.sourceURL) || isBuildAsset(error?.fileName))) {
        return true;
    }

    return false;
};

// Recovering from a chunk load error means reloading the page, which only helps
// if the retry succeeds. Cap consecutive attempts so an asset that is
// persistently unparseable degrades to the ErrorBoundary's crash screen instead
// of looping. Attempts further apart than RELOAD_WINDOW_MS aren't consecutive —
// a real loop reloads within seconds, an unrelated failure an hour later should
// still get its own retry.
const RELOAD_GUARD_KEY = 'chunkReloadGuard';
const MAX_CONSECUTIVE_RELOADS = 2;
const RELOAD_WINDOW_MS = 10000;

const readReloadGuard = () => {
    try {
        const {count, at} = JSON.parse(sessionStorage.getItem(RELOAD_GUARD_KEY)) || {};
        return count && Date.now() - at <= RELOAD_WINDOW_MS ? count : 0;
    } catch (e) {
        // Blocked/absent sessionStorage, or a malformed value — start fresh.
        return 0;
    }
};

/**
 * Whether a chunk-load reload is still allowed. Peeks at the guard without
 * consuming an attempt, so the ErrorBoundary fallback can choose between the
 * "reloading" spinner and the crash screen.
 * @returns {boolean}
 */
export const canReloadForChunkError = () => readReloadGuard() < MAX_CONSECUTIVE_RELOADS;

/**
 * Reloads the page to recover from a chunk load error, unless we have already
 * retried too many times in a row.
 * @returns {boolean} true if a reload was triggered
 */
export const reloadForChunkError = () => {
    const count = readReloadGuard();

    if (count >= MAX_CONSECUTIVE_RELOADS) {
        console.error('Chunk load error persisted across reloads - showing the error screen');
        return false;
    }

    try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({count: count + 1, at: Date.now()}));
    } catch (e) {
        // Without storage we can't count attempts, so the cap degrades to
        // "always allow" — the behaviour we had before the guard existed.
    }

    console.log('Chunk load error detected - reloading page to fetch latest version');
    window.location.reload();
    return true;
};

/**
 * Reports an error to Sentry
 * @param {Error|string} error - The error to report
 * @param {Object} context - Additional context about the error
 */
export const reportError = (error, context = {}) => {
    // Always log to console for development
    console.error('Error reported:', error, context);

    // Report to Sentry
    const errorObj = typeof error === 'string' ? new Error(error) : error;

    Sentry.captureException(errorObj, {
        extra: context
    });
};

/**
 * Setup global error handlers
 */
export const setupGlobalErrorHandlers = () => {
    // Handle uncaught errors
    window.addEventListener('error', event => {
        const errorMessage = event.error?.message || event.message || '';
        const isResizeObserverError = errorMessage.includes('ResizeObserver');

        if (isResizeObserverError) {
            // Ignore ResizeObserver errors
            console.log('Ignoring ResizeObserver error');
            event.preventDefault();
            return;
        }

        // Check for chunk load errors (happens after deployments when cached
        // chunks are stale). If the reload cap is hit the error falls through to
        // Sentry, so a persistently broken asset stays visible.
        if (isChunkLoadError(event.error, event.filename) && reloadForChunkError()) {
            return;
        }

        // Report all other errors to Sentry
        reportError(event.error || event.message, {
            context: 'window.onerror',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            type: 'uncaught-error'
        });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
        const errorMessage = event.reason?.message || event.reason?.toString() || '';
        const isResizeObserverError = errorMessage.includes('ResizeObserver');

        if (isResizeObserverError) {
            // Ignore ResizeObserver errors
            console.log('Ignoring ResizeObserver error');
            event.preventDefault();
            return;
        }

        // Check for chunk load errors (happens after deployments when cached chunks are stale)
        if (isChunkLoadError(event.reason) && reloadForChunkError()) {
            return;
        }

        reportError(event.reason || 'Unhandled Promise Rejection', {
            context: 'unhandledrejection',
            promise: event.promise?.toString(),
            type: 'unhandled-rejection'
        });

        // Prevent default browser behavior (logging to console)
        // We're handling it ourselves
        event.preventDefault();
    });
};

/**
 * Wrapper for async functions that automatically reports errors
 * @param {Function} fn - The async function to wrap
 * @param {Object} context - Context to include in error reports
 * @returns {Function} Wrapped function
 */
export const withErrorReporting = (fn, context = {}) => {
    return async (...args) => {
        try {
            return await fn(...args);
        } catch (error) {
            reportError(error, {
                ...context,
                functionName: fn.name || 'anonymous'
            });
            throw error; // Re-throw so the caller can handle it
        }
    };
};
